import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AviationEvent, AviationEventTypeDesc, AviationEventType, TimeZones } from '../../shared/models/aviation-event.model';
import { MAT_DIALOG_DATA, MatDatepickerInputEvent, MatDialogRef, MatSnackBar } from '@angular/material';
import { AviationEventsService } from '../../shared/services/aviation-events.service';
import { eventAMPM, eventHours, eventMinutes, LOCAL_TZ } from '../../globals';
import * as MomentTZ from 'moment-timezone';
import { Router } from '@angular/router';
import { UserInfo } from '../../shared/models/user-info.model';
import { UserService } from '../../shared/user/user.service';

@Component({
  selector: 'app-clone-event',
  templateUrl: './clone-event.component.html',
  styleUrls: ['./clone-event.component.scss']
})
export class CloneEventComponent implements OnInit {
  formGroup: FormGroup;
  aviationEvent: AviationEvent;
  isLoading = true;
  eventTypes: String[];
  startDateMin: Date = new Date();
  endDateMin: Date = this.startDateMin;
  timeZones = Object.keys(TimeZones).sort();
  eventStart: MomentTZ;
  eventEnd: MomentTZ;
  eventStartDateString: string;
  eventStartTimeString: string;
  eventEndDateString: string;
  eventEndTimeString: string;
  startDate: Date;
  endDate: Date;
  private _userInfo: UserInfo;
  recId: number;
  eventHours = eventHours;
  eventMinutes = eventMinutes;
  eventAMPM = eventAMPM;


  constructor(private _matDialogRef: MatDialogRef<CloneEventComponent>,
              @Inject(MAT_DIALOG_DATA) data,
              private _aviationEventsService: AviationEventsService,
              private _fb: FormBuilder,
              private _snackBar: MatSnackBar,
              private _router: Router,
              private _userService: UserService) {
    this.aviationEvent = data.aviationEvent;
  }

  ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this._matDialogRef.close(null);
    });

    this._userInfo = this._userService.userInfo.getValue();
    if (this._userInfo) {
      this.recId = Number(this._userInfo.masterCustomerId);
    }
    this._userService.userInfo.subscribe(u => {
      // Update userInfo
      this._userInfo = u;
      if (u) {
        this.recId = Number(u.masterCustomerId);
      }
    });

    const currentTimeZoneSimpleName =  Object.keys(TimeZones).filter(it => TimeZones[it] === this.aviationEvent.timezone).pop();

    this.eventTypes = Object.values(AviationEventTypeDesc) as String[];
    const currentEventType = AviationEventTypeDesc[AviationEventType[this.aviationEvent.typeId]];
    this.eventStart = MomentTZ.tz(this.aviationEvent.startDateTime, 'UTC');
    this.eventStartDateString = this.eventStart.clone().tz(this.aviationEvent.timezone).format('dddd, MMMM Do, YYYY');
    this.eventStartTimeString = this.eventStart.clone().tz(this.aviationEvent.timezone).format('HH:mm');
    this.eventEnd = MomentTZ.tz(this.aviationEvent.endDateTime, 'UTC');
    this.eventEndDateString = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('dddd, MMMM Do, YYYY');
    this.eventEndTimeString = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('HH:mm');
    this.startDate = this.eventStart.clone().tz(this.aviationEvent.timezone).toDate();
    this.endDate = this.eventEnd.clone().tz(this.aviationEvent.timezone).toDate();

    this.formGroup = this._fb.group({
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      startHour: ['', Validators.required],
      startMin: ['', Validators.required],
      startAMPM: ['', Validators.required],
      endHour: ['', Validators.required],
      endMin: ['', Validators.required],
      endAMPM: ['', Validators.required],
      timezone: [currentTimeZoneSimpleName || '', Validators.required]
    }, {updateOn: 'submit'});
    this.isLoading = false;
  }

  showDateError(): boolean {
    const startDate: Date = this.formGroup.value.startDate;
    const endDate: Date = this.formGroup.value.endDate;
    let startHour: number = +this.formGroup.value.startHour;
    startHour = this.formGroup.value.startAMPM === 'PM' ? startHour + 12 : startHour;
    let endHour: number = +this.formGroup.value.endHour;
    endHour = this.formGroup.value.endAMPM === 'PM' ? endHour + 12 : endHour;

    const dateBoolean = startDate && endDate && startDate.getTime() > endDate.getTime();
    if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
      return startHour > endHour || (startHour === endHour && +this.formGroup.value.startMin > +this.formGroup.value.endMin);
    }
    return dateBoolean;
  }

  eventDateChange(type: string, event: MatDatepickerInputEvent<Date>) {
    const element = event.targetElement;
    if (element.getAttribute('name') === 'startDate') {
      this.endDateMin = event.value;
    }
  }

  private buildZonedTime(date: Date, time: string, timezone: String): string {
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const year = date.getFullYear();
    const localTime = MomentTZ.tz(`${year}-${month}-${day} ${time}`, timezone);
    return localTime.utc().format('MM/DD/YYYY h:mm:ss A');
  }

  close(result = {cancelBtnClicked: true, success: false}) {
    if (result) {
      this._matDialogRef.close(result);
    }
  }

  cancel() {
    this._matDialogRef.close(null);
  }

  async save () {
    const TEMP_aviationEvent = this.aviationEvent;
    const tzName = TimeZones[this.formGroup.value.timezone];
    const startDate = new Date(this.formGroup.value.startDate);
    let startTime = +this.formGroup.value.startHour < 10 ? '0' + this.formGroup.value.startHour : this.formGroup.value.startHour;
    // if startTime is 12:00 AM, set to 00:00
    if (+this.formGroup.value.startHour === 12 && this.formGroup.value.startAMPM === 'AM') {
      startTime = '00';
    } else {
      startTime = this.formGroup.value.startAMPM === 'PM' && +this.formGroup.value.startHour < 12 ? (+startTime + 12).toString() :
        startTime;
    }
    startTime = startTime + ':' + this.formGroup.value.startMin;
    const endDate = new Date(this.formGroup.value.endDate);
    let endTime = +this.formGroup.value.endHour < 10 ? '0' + this.formGroup.value.endHour : this.formGroup.value.endHour;
    // if endTime is 12:00 AM, set to 00:00
    if (+this.formGroup.value.endHour === 12 && this.formGroup.value.endAMPM === 'AM') {
      endTime = '00';
    } else {
      endTime = this.formGroup.value.endAMPM === 'PM' && +this.formGroup.value.endHour < 12 ? (+endTime + 12).toString() : endTime;
    }
    endTime = endTime + ':' + this.formGroup.value.endMin;

    if (this.formGroup.invalid) {
      this._snackBar.open('Form field validation failed. check form values', null, {duration: 5000});
      return;
    } else if (this.showDateError()) {
      this._snackBar.open('Start time cannot be after end time, check end time is not before start time', null, {duration: 5000});
      return;
    }

    // this.aviationEvent.format = this.formGroup.value.format;
    this.aviationEvent.timezone = tzName;
    this.aviationEvent.startDateTimeUTC = this.buildZonedTime(startDate, startTime, tzName);
    this.aviationEvent.endDateTimeUTC = this.buildZonedTime(endDate, endTime, tzName);

    // make api call
    try {
      const cloneResponse = await this._aviationEventsService.cloneAviationEvent(this.aviationEvent.eventId,
        this.aviationEvent.startDateTimeUTC, this.aviationEvent.endDateTimeUTC, this.recId);
      this.close({cancelBtnClicked: false, success: true});
      this._router.navigate(['/event', cloneResponse.eventId]);
    } catch (e) {
      this.aviationEvent = TEMP_aviationEvent;
      this.close({cancelBtnClicked: false, success: false});
    }
  }
}
